import React from 'react'
import invariant from 'invariant'
import debounce from 'debounce'
import { resetList, resetFilters, setFilter, setSort, setPage, setPageLimit } from './actions'

const isReactNative =
  typeof navigator === 'object' &&
  navigator.product === 'ReactNative' // eslint-disable-line no-undef

const isEmpty = (value) => {
  if (typeof value === 'undefined' || value === '' || value === null) return true
  if (value.length > 0) return false
  if (value.length === 0) return true
  if (typeof value === 'object' && Object.keys(value).length === 0) return true
  return false
}

// Higher order component for display of filtered, sorted and paginated data
const lists = (WrappedComponent, providedOptions) => {
  const options = Object.assign({
    filters: [],
    headings: [],
    debounceTime: 500,
    maxPages: 10,
    limit: 100,
    otherParams: {},
    getInitialState: () => ({}),
  }, providedOptions)

  const {
    path,
    filters,
    headings,
    debounceTime,
    maxPages,
    limit,
    loadDataAction,
    getInitialState,
  } = options

  invariant(Array.isArray(filters), 'Fields must be an array.')
  invariant(
    (typeof path === 'string') ||
    (typeof path === 'function') ||
    Array.isArray(path)
  , 'Path must be a string, function, or an array.')

  const getNormalizedPath = (props, location) => {
    let normalizedPath
    switch (typeof path) {
      case 'function':
        normalizedPath = path(props)
        break
      case 'string':
        normalizedPath = [path]
        break
      default:
        normalizedPath = path
    }
    if (location) normalizedPath.push(location)
    return normalizedPath
  }

  return class List extends React.Component {

    static contextTypes = {
      store: React.PropTypes.object, // Redux store.
    }

    static getSortValue(model, initialState) {
      if (model && model.has('sort')) {
        return model.get('sort')
      }
      if (initialState && {}.hasOwnProperty.call(initialState, 'sort')) {
        return initialState.sort
      }
      return ''
    }

    static getLimitValue(model, initialState) {
      if (model && model.has('limit')) {
        return model.get('limit')
      }
      if (initialState && {}.hasOwnProperty.call(initialState, 'limit')) {
        return initialState.limit
      }
      return limit
    }

    static getPageValue(model, initialState) {
      if (model && model.has('page')) {
        return model.get('page')
      }
      if (initialState && {}.hasOwnProperty.call(initialState, 'page')) {
        return initialState.page
      }
      return 1
    }

    static getFilterValue(filter, model, initialState) {
      if (
        model
        && model.has('filters')
        && model.get('filters').has(filter)
      ) {
        return model.get('filters').get(filter)
      }
      if (
        initialState
        && {}.hasOwnProperty.call(initialState, 'filters')
        && {}.hasOwnProperty.call(initialState.filters, filter)
      ) {
        return initialState.filters[filter]
      }
      return ''
    }

    static lazyJsonValuesOf(model, props) {
      const initialState = getInitialState && getInitialState(props)
      const filters = options.filters.reduce((filters, filter) => ({
        ...filters,
        [filter]: List.getFilterValue(filter, model, initialState),
      }), Object.create(null))
      const sort = List.getSortValue(model, initialState)
      const page = List.getPageValue(model, initialState)
      const limit = List.getLimitValue(model, initialState)
      return {
        filters,
        sort,
        page,
        limit,
      }
    }

    static prepareQuery(values) {
      const { filters, sort, page, limit } = values
      const parsedFilters = {}
      Object.keys(filters).forEach(key => {
        if (isEmpty(filters[key])) return

        const parts = key.split('.')
        const lastPart = parts.pop()
        if ([
          'from',
          'to',
          'in',
          'like', 'likeStart', 'likeEnd',
          'exists',
          'likeCollate', 'likeCollateStart', 'likeCollateEnd',
          'likeFold'
        ].indexOf(lastPart) !== -1) {
          const filter = { [lastPart]: filters[key] }
          const filterKey = parts.join('.')
          parsedFilters[filterKey] = Object.assign({}, parsedFilters[filterKey], filter)
        } else {
          parsedFilters[key] = filters[key]
        }
      })
      return {
        filters: parsedFilters,
        sort,
        page,
        perPage: limit,
      }
    }

    static createFilterObject(filter, onChange) {
      return isReactNative ? {
        onChangeText: text => {
          onChange(filter, text)
        },
      } : {
        onChange: (event) => {
          const target = event.target || event
          const { type, checked, value } = target
          const isCheckbox = type && type.toLowerCase() === 'checkbox'
          onChange(filter, isCheckbox ? checked : value)
        },
      }
    }

    static createHeadingObject(heading, onClick) {
      return isReactNative ? {
        onPress: () => {
          onClick(heading)
        },
      } : {
        onClick: () => {
          onClick(heading)
        },
      }
    }

    state = {
      model: null,
    }

    onFilterChange = (filter, value) => {
      const normalizedPath = getNormalizedPath(this.props, 'filters').concat(filter)
      this.context.store.dispatch(setFilter(normalizedPath, value))
      this.list.$setPage(1)
    }

    onHeadingClick = (heading) => {
      const normalizedPath = getNormalizedPath(this.props, 'sort')
      this.context.store.dispatch(setSort(normalizedPath, heading))
    }

    onPagingClick = (page) => this.list.$setPage(page)

    createPaging = (itemCount) => {
      const limit = this.values.limit
      const pages = Math.ceil(itemCount / limit)
      if (pages < 2) return null
      const currentPage = this.values.page || 1
      const items = []
      // move backwards
      items.push({
        first: true,
      })
      if (currentPage === 1) {
        items[0].disabled = true
      } else {
        items[0].onClick = () => { this.onPagingClick(currentPage - 1) }
      }
      let fixedItems = 0
      // first and second page are fixed when there is more pages then limit
      if (pages > maxPages) {
        if (currentPage > 1) {
          items.push({
            text: 1,
            onClick: () => { this.onPagingClick(1) },
          })
          fixedItems = 1
        }
        if (currentPage > 2) {
          items.push({
            disabled: true,
            text: '...',
          })
          fixedItems = 2
        }
        const remainingPages = (pages + 1) - currentPage
        const remainingMenuItems = maxPages - fixedItems // remove page 1 and 2
        for (let i = 0; i < remainingMenuItems; i += 1) {
          let offset
          if (remainingPages >= remainingMenuItems) offset = i
          else offset = (remainingPages) - (remainingMenuItems - i)
          items.push({
            text: currentPage + offset,
            active: offset === 0,
            onClick: () => { this.onPagingClick(currentPage + offset) },
          })
        }
      } else {
        for (let i = 1; i <= pages; i += 1) {
          items.push({
            text: i,
            active: currentPage === i,
            onClick: () => { this.onPagingClick(i) },
          })
        }
      }
      // move forward
      items.push({
        last: true,
      })

      if (currentPage === pages) items[items.length - 1].disabled = true
      else items[items.length - 1].onClick = () => { this.onPagingClick(currentPage + 1) }

      return items
    }

    createList() {
      const filters = options.filters.reduce((filters, filter) => ({
        ...filters,
        [filter]: List.createFilterObject(filter, this.onFilterChange),
      }), {})

      const headings = options.headings.reduce((headings, heading) => ({
        ...headings,
        [heading]: List.createHeadingObject(heading, this.onHeadingClick),
      }), {})

      this.list = {
        filters: {
          ...filters,
        },
        headings: {
          ...headings,
        },
        $paging: this.createPaging,
        $query: () => this.getQuery(),
        $sortBy: (field) => {
          const normalizedPath = getNormalizedPath(this.props, 'sort')
          this.context.store.dispatch(setSort(normalizedPath, field))
        },
        $setPage: (page) => {
          const normalizedPath = getNormalizedPath(this.props, 'page')
          this.context.store.dispatch(setPage(normalizedPath, page))
        },
        $setPageLimit: (limit) => {
          const normalizedPath = getNormalizedPath(this.props, 'limit')
          this.context.store.dispatch(setPageLimit(normalizedPath, limit))
        },
        $resetFilters: () => {
          const normalizedPath = getNormalizedPath(this.props, 'filters')
          this.context.store.dispatch(resetFilters(normalizedPath))
        },
        $reset: () => {
          const normalizedPath = getNormalizedPath(this.props)
          this.context.store.dispatch(resetList(normalizedPath))
        },
        $heading: (heading, text, icons) => {
          let icon
          switch (this.list.headings[heading].direction) {
            case 1:
              icon = icons.descending
              break
            case -1:
              icon = icons.ascending
              break
            default:
              icon = icons.sort
              break
          }
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions
          return <a onClick={this.list.headings[heading].onClick}>{text} {icon}</a>
        },
      }
    }

    getQuery() {
      const values = List.lazyJsonValuesOf(this.state.model, this.props)
      return List.prepareQuery(values)
    }

    loadData() {
      const query = this.getQuery()
      loadDataAction(this.props)(query)
    }

    getModelFromState() {
      const normalizedPath = getNormalizedPath(this.props)
      return this.context.store.getState().lists.getIn(normalizedPath)
    }

    setModel(model) {
      this.values = List.lazyJsonValuesOf(model, this.props)
      if (!this.list.filters) this.list.filters = {}
      filters.forEach(filter => {
        this.list.filters[filter].value = this.values.filters[filter]
      })
      if (!this.list.headings) this.list.headings = {}
      headings.forEach(heading => {
        let direction
        switch (this.values.sort) {
          case heading:
            direction = 1
            break
          case `-${heading}`:
            direction = -1
            break
          default:
            direction = 0
        }
        this.list.headings[heading].direction = direction
      })
      this.list.page = this.values.page
      this.list.limit = this.values.limit
      this.list.sort = this.values.sort
      this.list = { ...this.list } // Ensure rerender for pure components.
      this.setState({ model })
    }

    componentWillMount() {
      this.createList()
      this.setModel(this.getModelFromState())
      this.update = () => {
        clearTimeout(this.reloadTimeout)
        this.reloadTimeout = setTimeout(() => { this.loadData() }, debounceTime)
      }
      this.update()
    }

    componentDidMount() {
      const { store } = this.context
      this.unsubscribe = store.subscribe(() => {
        const newModel = this.getModelFromState()
        if (newModel === this.state.model) return
        this.setModel(newModel)
        this.update()
      })
    }

    componentWillUnmount() {
      clearTimeout(this.reloadTimeout)
      this.unsubscribe()
      this.list = null
    }

    render() {
      return (
        <WrappedComponent {...this.props} list={this.list} />
      )
    }

  }
}

export default lists



// WEBPACK FOOTER //
// ./src/common/lib/redux-lists/lists.js